import { useState, useMemo, useCallback, useLayoutEffect, useRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/tailwindadmin/components/ui/select'
import { Download, Search } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import type { AuditListItem } from '@/lib/equip-types'
import { useProviderAudits } from '@/hooks/equip/useEquipAudits'
import { equipTable } from '@/views/equip/equip-table-styles'
import { SortableHeader } from '@/views/equip/components/SortableHeader'
import { cn } from '@/lib/utils'

interface AuditGridProps {
  providerEmail: string
  auditType: string
  year: number
  quarter: number
  onAuditClick: (auditId: number, auditType: string) => void
  providerName?: string
  embedded?: boolean
  /** @deprecated No longer used — AuditGrid owns its loading state via React Query */
  isLoading?: boolean
}

export default function AuditGrid({
  providerEmail,
  auditType,
  year,
  quarter,
  onAuditClick,
  providerName: _providerName,
  embedded = false,
}: AuditGridProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // React Query: fetch audits — cache hit within 5 minutes produces no network request
  const { data: audits = [], isLoading } = useProviderAudits(
    providerEmail,
    auditType,
    year,
    quarter
  )

  // Create column helper
  const columnHelper = createColumnHelper<AuditListItem>()

  // Badge variant based on score
  const getBadgeVariant = (score: number) => {
    if (score >= 89) return 'lightSuccess'
    if (score >= 70) return 'lightWarning'
    return 'lightError'
  }

  // Audit columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('auditDate', {
        header: ({ column }) => <SortableHeader label="Audit Date" column={column} />,
        cell: ({ row }) => {
          if (!row.original.auditDate) return <p className="text-sm">{'\u2014'}</p>
          try {
            const date = parseISO(row.original.auditDate)
            return (
              <p className="text-sm">{format(date, 'MM/dd/yyyy')}</p>
            )
          } catch {
            return <p className="text-sm">{'\u2014'}</p>
          }
        },
      }),
      columnHelper.accessor('percentage', {
        header: ({ column }) => <SortableHeader label="Score" column={column} />,
        cell: ({ row }) => {
          const score = row.original.percentage
          if (score === null || score === undefined) return <p className="text-sm">-</p>
          return (
            <Badge variant={getBadgeVariant(score)}>
              {score.toFixed(0)}%
            </Badge>
          )
        },
      }),
      columnHelper.accessor('chartId', {
        header: ({ column }) => <SortableHeader label="Note ID" column={column} />,
        cell: ({ row }) => (
          <p className="text-sm">{row.original.chartId ?? '-'}</p>
        ),
      }),
      columnHelper.accessor('patientName', {
        header: ({ column }) => <SortableHeader label="Patient" column={column} />,
        cell: ({ row }) => (
          <p className="text-sm">{row.original.patientName ?? '-'}</p>
        ),
      }),
    ],
    [columnHelper]
  )

  const table = useReactTable({
    data: audits,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  // CSV export handler
  const handleExportCSV = useCallback(() => {
    const headers = ['Audit Date', 'Score', 'Note ID', 'Patient']
    const rows = audits.map((a) => {
      let dateStr = a.auditDate ?? '\u2014'
      try {
        if (a.auditDate) {
          const date = parseISO(a.auditDate)
          dateStr = format(date, 'MM/dd/yyyy')
        }
      } catch {
        dateStr = '\u2014'
      }
      return [
        dateStr,
        a.percentage !== null && a.percentage !== undefined ? `${a.percentage.toFixed(0)}%` : '-',
        a.chartId ?? '-',
        a.patientName ?? '-',
      ]
    })
    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `${auditType}-audits-${providerEmail}-${year}-Q${quarter}.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [audits, providerEmail, auditType, year, quarter])

  // Dynamically size the scroll container to fill the remaining viewport height (skip when embedded)
  useLayoutEffect(() => {
    if (embedded) return
    const el = scrollContainerRef.current
    if (!el) return
    const recalc = () => {
      const top = el.getBoundingClientRect().top
      const maxH = Math.max(200, window.innerHeight - top - 30)
      el.style.maxHeight = `${maxH}px`
    }
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [audits, embedded])

  if (isLoading) {
    if (embedded) {
      return <p className="text-sm text-muted-foreground py-4">Loading audits...</p>
    }
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Loading audits...</p>
      </Card>
    )
  }

  const Wrapper = embedded ? 'div' : Card
  const wrapperProps = embedded ? { className: 'space-y-4' } : { className: 'p-6' }

  return (
    <Wrapper {...wrapperProps}>
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {auditType === 'coding' ? 'Coding' : 'Signature Standards'} Audits
          </h3>
          <div className="flex items-center gap-2">
            {/* Search toggle */}
            {!showSearch ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSearch(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            ) : (
              <Input
                placeholder="Search audits..."
                className="w-56"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                onBlur={() => {
                  if (!globalFilter) setShowSearch(false)
                }}
              />
            )}
            {/* CSV export */}
            <Button variant="ghost" size="icon" onClick={handleExportCSV}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className={equipTable.wrapper}>
          <div ref={scrollContainerRef} className="overflow-auto">
            <AnimatedTableWrapper className="overflow-x-auto">
              <table className={equipTable.table}>
                <thead className={equipTable.thead}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className={equipTable.theadTr}>
                      {headerGroup.headers.map((header, headerIdx) => (
                        <th
                          key={header.id}
                          className={cn(equipTable.th, headerIdx === 0 && equipTable.stickyTh)}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <AnimatedTableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-4 text-muted-foreground"
                      >
                        No {auditType === 'coding' ? 'coding' : 'signature standards'} audits found for this quarter
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row, index) => (
                      <AnimatedTableRow
                        key={row.id}
                        index={index}
                        className={`${equipTable.trClickable} ${equipTable.trAlt(index)}`}
                        onClick={() =>
                          onAuditClick(row.original.auditId, row.original.auditType)
                        }
                      >
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td
                            key={cell.id}
                            className={cn(
                              equipTable.td,
                              cellIdx === 0 && (index % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd)
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </AnimatedTableRow>
                    ))
                  )}
                </AnimatedTableBody>
              </table>
            </AnimatedTableWrapper>
          </div>
        </div>

        {/* Pagination Controls */}
        {table.getPageCount() > 0 && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted">Show</p>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="w-fit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted">per page</p>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted">
                {table.getRowModel().rows.length > 0
                  ? `${
                      table.getState().pagination.pageIndex *
                        table.getState().pagination.pageSize +
                      1
                    }-${Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                        table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length
                    )} of ${table.getFilteredRowModel().rows.length}`
                  : '0 of 0'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="h-8 w-8"
                >
                  ←
                </Button>
                <span className="text-sm font-medium">
                  {table.getState().pagination.pageIndex + 1}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="h-8 w-8"
                >
                  →
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Wrapper>
  )
}
